/**
 * Color manipulation utilities.
 *
 * Provides hex color parsing, luminance calculation, and contrast functions
 * for accessibility and visual design.
 */

// =============================================================================
// TYPES
// =============================================================================

export interface RGB {
  r: number;
  g: number;
  b: number;
}

// =============================================================================
// PARSING
// =============================================================================

/**
 * Parse a hex color string to RGB values.
 *
 * @param hex - Hex color string (e.g., "#ff5500" or "ff5500")
 * @returns RGB object or null if invalid
 */
export function hexToRgb(hex: string): RGB | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return null;
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
}

// =============================================================================
// LUMINANCE
// =============================================================================

/**
 * Calculate relative luminance of a hex color.
 *
 * Uses the standard formula: (0.299*R + 0.587*G + 0.114*B) / 255
 * Returns value between 0 (black) and 1 (white).
 *
 * @param hex - Hex color string (e.g., "#ff5500")
 * @returns Luminance value (0-1), defaults to 0.5 if parsing fails
 */
export function getLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0.5;
  return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
}

// =============================================================================
// CONTRAST
// =============================================================================

/** Cache for contrasting icon colors by background hex */
const contrastColorCache = new Map<string, string>();

/**
 * Get contrasting icon color (black or white) based on background luminance.
 *
 * Uses luminance threshold of 0.5 for accessibility.
 * Results are memoized by background color for performance.
 *
 * @param bgHex - Background color hex string
 * @returns '#000000' for light backgrounds, '#ffffff' for dark backgrounds
 */
export function getContrastingIconColor(bgHex: string): string {
  let result = contrastColorCache.get(bgHex);
  if (!result) {
    result = getLuminance(bgHex) > 0.5 ? '#000000' : '#ffffff';
    contrastColorCache.set(bgHex, result);
  }
  return result;
}

// =============================================================================
// COLOR MANIPULATION
// =============================================================================

/**
 * Desaturate a hex color by a given amount.
 *
 * @param hex - Hex color string (e.g., "#ff5500")
 * @param saturation - Saturation multiplier (0.0 = grey, 1.0 = original)
 * @returns Desaturated hex color
 */
export function desaturateColor(hex: string, saturation: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) {
    return hex;
  }

  // Calculate luminance for greyscale conversion
  const luminance = 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b;

  // Interpolate between greyscale and original color
  const newR = Math.round(luminance + (rgb.r - luminance) * saturation);
  const newG = Math.round(luminance + (rgb.g - luminance) * saturation);
  const newB = Math.round(luminance + (rgb.b - luminance) * saturation);

  // Convert back to hex
  const toHex = (c: number) => {
    const hex = Math.max(0, Math.min(255, c)).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(newR)}${toHex(newG)}${toHex(newB)}`;
}
